import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import MainWebsite from './pages/MainWebsite';
import AdminLogin from './pages/AdminLogin';
import {
  getSiteData,
  updateSiteData as updateSiteDataInDB,
  getBlogPosts,
  addBlogPost,
  getServices,
  getTestimonials,
  getResources
} from './services/dataService';
import 'bootstrap-icons/font/bootstrap-icons.css';

function App() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Default data structure
  const defaultData = {
    hero: {
      title: "Welcome to Your Healing Journey",
      subtitle: "Professional therapy services to help you navigate life's challenges with compassion and expertise.",
      buttonText: "Schedule Consultation"
    },
    about: {
      title: "About My Practice",
      content: "With over 10 years of experience in mental health counseling, I provide a safe, supportive environment for individuals seeking positive change.",
      image: "/api/placeholder/400/500"
    },
    services: [
      {
        id: 1,
        title: "Individual Therapy",
        description: "One-on-one sessions tailored to your specific needs and goals.",
        icon: "👤"
      },
      {
        id: 2,
        title: "Couples Counseling",
        description: "Strengthen relationships through guided communication and understanding.",
        icon: "❤️"
      },
      {
        id: 3,
        title: "Family Therapy",
        description: "Improve family dynamics and create healthier relationships.",
        icon: "👨‍👩‍👧‍👦"
      }
    ],
    testimonials: [
      {
        id: 1,
        name: "Sarah M.",
        text: "The therapy sessions have been life-changing. I feel more confident and at peace.",
        rating: 5
      },
      {
        id: 2,
        name: "John D.",
        text: "Professional, caring, and effective. Highly recommend these services.",
        rating: 5
      }
    ],
    blog: [
      {
        id: 1,
        title: "Understanding Anxiety in Modern Life",
        excerpt: "Explore the common triggers of anxiety and practical coping strategies.",
        date: "2024-01-15",
        category: "Mental Health"
      },
      {
        id: 2,
        title: "Building Healthy Relationships",
        excerpt: "Key principles for maintaining strong, supportive relationships.",
        date: "2024-01-10",
        category: "Relationships"
      }
    ],
    resources: [
      {
        id: 1,
        title: "Crisis Hotlines",
        description: "24/7 support when you need it most",
        links: ["National Suicide Prevention Lifeline: 988"]
      },
      {
        id: 2,
        title: "Self-Care Resources",
        description: "Tools and techniques for daily mental wellness",
        links: ["Meditation apps", "Breathing exercises", "Journaling prompts"]
      }
    ],
    contact: {
      phone: "(555) 123-4567",
      email: "contact@therapist.com",
      address: "123 Wellness Street, City, State 12345",
      hours: "Monday - Friday: 9AM - 6PM"
    },
    general: {
      logo: null,
      logoPath: null,
      officePhoto: null,
      officePhotoPath: null,
      profilePhoto: null,
      profilePhotoPath: null
    },

    contact: {
      phone: "(555) 123-4567",
      email: "contact@therapist.com",
      address: "123 Wellness Street, City, State 12345",
      hours: "Monday - Friday: 9AM - 6PM"
    },
    navbar: {
      name: "BEN COBOS",
      title: "Marriage & Family Therapist",
      nameColor: "#92400e", // amber-800
      titleColor: "#57534e", // stone-600
      navTextColor: "#4b5563", // gray-600
      activeNavColor: "#92400e", // amber-700
      hoverColor: "#92400e" // amber-700
    },
  };

  const [siteData, setSiteData] = useState(defaultData);

  // Load data from Firebase on component mount
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Load main site data
        const mainData = await getSiteData();

        // Load collections in parallel
        const [blogPosts, services, testimonials, resources] = await Promise.all([
          getBlogPosts(),
          getServices(),
          getTestimonials(),
          getResources()
        ]);

        let finalBlogPosts = blogPosts;

        // Create default blog posts if none exist
        if (blogPosts.length === 0) {
          console.log('No blog posts found, creating defaults...');
          const createdPosts = [];

          for (const defaultPost of defaultData.blog) {
            try {
              const { id, ...postData } = defaultPost; // Remove the default ID
              const docId = await addBlogPost(postData);
              console.log(`Created blog post with ID: ${docId}`);
              createdPosts.push({ ...postData, id: docId });
            } catch (error) {
              console.error('Error creating default blog post:', error);
            }
          }

          finalBlogPosts = createdPosts;
        }

        // Merge Firebase data with defaults
        const loadedData = {
          hero: mainData.hero || defaultData.hero,
          about: mainData.about || defaultData.about,
          contact: mainData.contact || defaultData.contact,
          general: mainData.general || defaultData.general,
          navbar: mainData.navbar || defaultData.navbar, // Add this line
          blog: finalBlogPosts,
          services: services.length > 0 ? services : defaultData.services,
          testimonials: testimonials.length > 0 ? testimonials : defaultData.testimonials,
          resources: resources.length > 0 ? resources : defaultData.resources
        };

        setSiteData(loadedData);
      } catch (error) {
        console.error('Error loading data:', error);
        setError('Failed to load data from server. Using default content.');
        setSiteData(defaultData);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  const updateSiteData = async (section, data) => {
    try {
      console.log('📱 App.js updateSiteData called:', { section, data });

      // Update local state immediately for responsive UI
      setSiteData(prev => {
        const newState = {
          ...prev,
          [section]: data
        };
        console.log('📱 App.js new state:', newState);
        return newState;
      });

      // Update Firebase with just the section data
      await updateSiteDataInDB(section, data);
      console.log('📱 Firebase update completed');
    } catch (error) {
      console.error('Error updating site data:', error);
      setError('Failed to save changes. Please try again.');

      // Revert local state if Firebase update fails
      setSiteData(prev => ({
        ...prev,
        [section]: prev[section]
      }));
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-stone-800 mx-auto mb-4"></div>
          <p className="text-stone-600">Loading your website...</p>
        </div>
      </div>
    );
  }

  return (
    <Router>
      <Routes>
        {/* Main Website Route */}
        <Route
          path="/"
          element={
            <MainWebsite
              siteData={siteData}
              error={error}
            />
          }
        />

        {/* Admin Login Route */}
        <Route
          path="/admin"
          element={
            <AdminLogin
              siteData={siteData}
              updateSiteData={updateSiteData}
              setSiteData={setSiteData}
            />
          }
        />

        {/* Catch all route - redirect to home */}
        <Route path="*" element={<MainWebsite siteData={siteData} error={error} />} />
      </Routes>
    </Router>
  );
}

export default App;